import { useEffect } from 'react';
import { useQuery } from 'react-query';
import { RouterProvider } from 'react-router-dom';

import Footer from './components/footer/Footer';
import Header from './components/header/Header';
import Loader from './components/loader/Loader';
import { router } from './router';
import { ROUTER_URL } from './router.constant';
import { getWebToken } from './services/api/api.service';
import { getNotAllowedFlag, redirectToNotSignedInPage, removeWebToken, validateToken } from './services/auth.service';
import FooterRefProvider from './utils/FooterRefProvider';
import OffsetProvider from './utils/OffsetProvider';

function App(): JSX.Element {
  const onAuthTokenDetailsError = (): void => {
    removeWebToken();
    redirectToNotSignedInPage();
  };

  // Check if we're on the error page and have notAllowed flag or 401 (not signed in) to prevent redirect loop
  const isOnErrorPage = window.location.pathname.includes(`/${ROUTER_URL.ERROR}`);
  const hasNotAllowedFlag = !!getNotAllowedFlag();
  const isNotSignedInPage = window.location.search.includes('statusCode=401');
  const shouldSkipAuth = isOnErrorPage && (hasNotAllowedFlag || isNotSignedInPage);

  // Match cd-data-rewrite: refresh.timeout.interval.admin/others=180 (seconds).
  // Do NOT refetch on an interval – that would reset the server’s inactivity timer and session would never expire.
  // Refetch only on window focus so after 180s inactive, when user returns we get session expired and logout.
  const { isLoading } = useQuery('auth-token-details', () => getWebToken(), {
    onSuccess: ({ jwtToken }) => validateToken(jwtToken),
    onError: () => onAuthTokenDetailsError(),
    enabled: !shouldSkipAuth,
    refetchOnWindowFocus: true,
    useErrorBoundary: false,
  });

  // When user logs out in another tab, webToken is removed from localStorage.
  // This tab receives a storage event so we logout here too (multi-tab sync).
  // Does not affect 180s inactive session – no refetch interval is used.
  useEffect(() => {
    const handleStorageChange = (event: StorageEvent): void => {
      if (event.key === 'webToken' && event.newValue === null && event.oldValue !== null) {
        removeWebToken();
        redirectToNotSignedInPage();
      }
    };

    window.addEventListener('storage', handleStorageChange);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
    };
  }, []);

  if (isLoading) {
    return <Loader fullPage />;
  }

  return (
    <OffsetProvider>
      <FooterRefProvider>
        <Header />
        <RouterProvider router={router} />
        <Footer />
      </FooterRefProvider>
    </OffsetProvider>
  );
}

export default App;
